<!-- Page Header -->
<div class="mb-4">
  <h1 class="h2 mb-1 fw-bold">FAA Reports</h1>
  <p class="text-muted mb-0">Generate compliance reports for FAA regulatory requirements</p>
</div>

<!-- Error Alert -->
<div *ngIf="error()" class="alert alert-danger alert-dismissible fade show mb-4" role="alert">
  <i class="bi bi-exclamation-triangle-fill me-2"></i>
  {{ error() }}
  <button type="button" class="btn-close" (click)="clearError()"></button>
  <div class="mt-2">
    <button class="btn btn-sm btn-outline-danger" (click)="retryLoad()">
      <i class="bi bi-arrow-clockwise me-1"></i>Retry
    </button>
  </div>
</div>

<div class="row g-4">
  <!-- Report Generator -->
  <div class="col-lg-5">
    <div class="card">
      <div class="card-header p-3">
        <h5 class="mb-0"><i class="bi bi-file-earmark-plus me-2"></i>Generate Report</h5>
      </div>
      <div class="card-body">
        <!-- Date Range -->
        <div class="mb-4">
          <label class="form-label fw-medium">Date Range</label>
          <div class="row g-2">
            <div class="col-6">
              <label class="form-label small text-muted">Start Date</label>
              <input type="date" class="form-control" [(ngModel)]="startDate">
            </div>
            <div class="col-6">
              <label class="form-label small text-muted">End Date</label>
              <input type="date" class="form-control" [(ngModel)]="endDate">
            </div>
          </div>
        </div>

        <!-- Report Type -->
        <div class="mb-4">
          <label class="form-label fw-medium">Report Type</label>
          <select class="form-select" [(ngModel)]="reportType">
            <option *ngFor="let type of reportTypes" [value]="type.value">
              {{ type.label }}
            </option>
          </select>
        </div>

        <!-- Include Options -->
        <div class="mb-4">
          <label class="form-label fw-medium">Include in Report</label>
          <div class="form-check mb-2">
            <input class="form-check-input" type="checkbox" id="flightLogs" [(ngModel)]="includeFlightLogs">
            <label class="form-check-label" for="flightLogs">Flight Logs</label>
          </div>
          <div class="form-check mb-2">
            <input class="form-check-input" type="checkbox" id="sprayRecords" [(ngModel)]="includeSprayRecords">
            <label class="form-check-label" for="sprayRecords">Spray Records</label>
          </div>
          <div class="form-check mb-2">
            <input class="form-check-input" type="checkbox" id="pilotInfo" [(ngModel)]="includePilotInfo">
            <label class="form-check-label" for="pilotInfo">Pilot Information</label>
          </div>
          <div class="form-check">
            <input class="form-check-input" type="checkbox" id="aircraftInfo" [(ngModel)]="includeAircraftInfo">
            <label class="form-check-label" for="aircraftInfo">Aircraft Information</label>
          </div>
        </div>

        <!-- Generate Button -->
        <button
          class="btn btn-primary w-100"
          (click)="generateReport()"
          [disabled]="isGenerating()"
        >
          <ng-container *ngIf="!isGenerating()">
            <i class="bi bi-file-earmark-pdf me-2"></i>Generate PDF Report
          </ng-container>
          <ng-container *ngIf="isGenerating()">
            <span class="spinner-border spinner-border-sm me-2" role="status"></span>
            Generating...
          </ng-container>
        </button>
      </div>
    </div>

    <!-- Info Card -->
    <div class="card mt-4">
      <div class="card-body">
        <h6 class="d-flex align-items-center gap-2 mb-3">
          <i class="bi bi-info-circle text-primary"></i>
          About FAA Reports
        </h6>
        <p class="small text-muted mb-2">
          FAA reports compile your operational data into formats suitable for regulatory compliance and audits.
        </p>
        <ul class="small text-muted mb-0 ps-3">
          <li>Part 107 reports include remote pilot certificate info</li>
          <li>Part 137 reports detail agricultural operations</li>
          <li>Waiver reports track night and swarm operations</li>
          <li>All reports are exportable as PDF</li>
        </ul>
      </div>
    </div>
  </div>

  <!-- Recent Reports -->
  <div class="col-lg-7">
    <div class="card">
      <div class="card-header d-flex justify-content-between align-items-center p-3">
        <h5 class="mb-0">Recent Reports</h5>
        <span class="badge bg-secondary">{{ recentReports().length }} reports</span>
      </div>
      <div class="card-body p-0">
        <!-- Loading State -->
        <ng-container *ngIf="isLoading()">
          <div class="p-5 text-center">
            <div class="spinner-border text-primary mb-3" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="text-muted mb-0">Loading reports...</p>
          </div>
        </ng-container>

        <!-- Reports List -->
        <ng-container *ngIf="!isLoading()">
          <ng-container *ngIf="recentReports().length > 0; else emptyState">
            <ng-container *ngFor="let report of recentReports(); let last = last">
              <div class="report-item p-3" [class.border-bottom]="!last">
                <div class="d-flex justify-content-between align-items-start">
                  <div class="d-flex align-items-start gap-3 flex-grow-1">
                    <div class="report-icon" [ngClass]="'status-' + report.status">
                      <i class="bi" [ngClass]="getStatusIcon(report.status)"
                         [class.spin]="report.status === 'generating'"></i>
                    </div>
                    <div class="flex-grow-1">
                      <div class="d-flex align-items-center gap-2 mb-1 flex-wrap">
                        <h6 class="mb-0 fw-medium">{{ report.name || getReportTypeLabel(report.reportType) }}</h6>
                        <span class="badge" [ngClass]="report.status | statusBadge:'report'">
                          <i class="bi me-1" [ngClass]="getStatusIcon(report.status)"
                             [class.spin]="report.status === 'generating'"></i>
                          {{ getStatusText(report.status) }}
                        </span>
                      </div>
                      <div class="small text-muted mb-1">{{ formatDateRange(report.startDate, report.endDate) }}</div>

                      <!-- Progress bar for generating status -->
                      <div *ngIf="report.status === 'generating'" class="progress-container mb-2">
                        <div class="progress h-6px">
                          <div class="progress-bar progress-bar-striped progress-bar-animated"
                               role="progressbar"
                               [style.width.%]="report.generationProgress || 0"
                               [attr.aria-valuenow]="report.generationProgress || 0"
                               aria-valuemin="0"
                               aria-valuemax="100">
                          </div>
                        </div>
                        <span class="small text-muted">{{ report.generationProgress || 0 }}% complete</span>
                      </div>

                      <!-- Error message for failed reports -->
                      <div *ngIf="report.status === 'failed' && report.errorMessage" class="error-info mb-1">
                        <span class="small text-danger">
                          <i class="bi bi-exclamation-circle me-1"></i>
                          {{ report.errorMessage }}
                        </span>
                      </div>

                      <!-- Submission info for submitted reports -->
                      <div *ngIf="report.status === 'submitted' && report.submittedTo" class="py-1 mb-1">
                        <span class="small text-success">
                          <i class="bi bi-check-circle me-1"></i>
                          Sent to {{ report.submittedTo }} on {{ report.submittedAt | adnDate:'datetime' }}
                        </span>
                      </div>

                      <div class="d-flex flex-wrap gap-3 small">
                        <div>
                          <i class="bi bi-clock me-1"></i>
                          {{ report.createdAt | adnDate:'datetime' }}
                        </div>
                        <div *ngIf="report.recordCount && report.recordCount > 0">
                          <i class="bi bi-list-ul me-1"></i>
                          {{ report.recordCount }} records
                        </div>
                        <div *ngIf="report.fileSize">
                          <i class="bi bi-file-earmark me-1"></i>
                          {{ report.fileSize }}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="flex-shrink-0 ms-3">
                    <div class="d-flex flex-column gap-2">
                      <!-- Action buttons based on status -->
                      <ng-container [ngSwitch]="report.status">
                        <!-- Draft: Generate PDF button -->
                        <ng-container *ngSwitchCase="'draft'">
                          <button class="btn btn-sm btn-primary" (click)="generatePdf(report)">
                            <i class="bi bi-file-earmark-pdf me-1"></i>Generate PDF
                          </button>
                          <button class="btn btn-sm btn-outline-secondary" (click)="viewReport(report)">
                            <i class="bi bi-eye me-1"></i>Preview
                          </button>
                        </ng-container>

                        <!-- Generating: Disabled with spinner -->
                        <ng-container *ngSwitchCase="'generating'">
                          <button class="btn btn-sm btn-outline-secondary" disabled>
                            <span class="spinner-border spinner-border-sm me-1"></span>
                            Generating...
                          </button>
                        </ng-container>

                        <!-- Ready: Download, View, Email buttons -->
                        <ng-container *ngSwitchCase="'ready'">
                          <div class="btn-group btn-group-sm">
                            <button class="btn btn-outline-primary" (click)="downloadReport(report)" title="Download PDF">
                              <i class="bi bi-download"></i>
                            </button>
                            <button class="btn btn-outline-secondary" (click)="viewReport(report)" title="Preview">
                              <i class="bi bi-eye"></i>
                            </button>
                            <button class="btn btn-outline-secondary" (click)="openEmailModal(report)" title="Send to Agency">
                              <i class="bi bi-envelope"></i>
                            </button>
                          </div>
                        </ng-container>

                        <!-- Submitted: Download, View, and re-send (nothing was sent by ADN) -->
                        <ng-container *ngSwitchCase="'submitted'">
                          <div class="btn-group btn-group-sm">
                            <button class="btn btn-outline-primary" (click)="downloadReport(report)" title="Download PDF">
                              <i class="bi bi-download"></i>
                            </button>
                            <button class="btn btn-outline-secondary" (click)="viewReport(report)" title="Preview">
                              <i class="bi bi-eye"></i>
                            </button>
                            <button class="btn btn-outline-secondary" (click)="openEmailModal(report)" title="Send to Agency Again">
                              <i class="bi bi-envelope"></i>
                            </button>
                          </div>
                        </ng-container>

                        <!-- Failed: Retry button -->
                        <ng-container *ngSwitchCase="'failed'">
                          <button class="btn btn-sm btn-warning" (click)="retryReport(report)">
                            <i class="bi bi-arrow-clockwise me-1"></i>Retry
                          </button>
                        </ng-container>
                      </ng-container>

                      <!-- Delete button (always available except during generation) -->
                      <button
                        *ngIf="report.status !== 'generating'"
                        class="btn btn-sm btn-outline-danger"
                        (click)="confirmDelete(report)"
                        title="Delete"
                      >
                        <i class="bi bi-trash"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </ng-container>
          </ng-container>

          <ng-template #emptyState>
            <div class="p-5 text-center">
              <div class="empty-state-icon mb-3">
                <i class="bi bi-file-earmark-text"></i>
              </div>
              <h5>No Reports Generated</h5>
              <p class="text-muted mb-0">Generate your first FAA report using the form on the left.</p>
            </div>
          </ng-template>
        </ng-container>
      </div>
    </div>
  </div>
</div>

<!-- Preview Modal -->
<div class="modal-backdrop fade show" *ngIf="showPreviewModal()" (click)="closePreviewModal()"></div>
<div class="modal fade show d-block" tabindex="-1" *ngIf="showPreviewModal()">
  <div class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable" (click)="$event.stopPropagation()">
    <div class="modal-content">
      <div class="modal-header">
        <h5 class="modal-title">
          <i class="bi bi-file-earmark-text me-2"></i>Report Preview
        </h5>
        <button type="button" class="btn-close" (click)="closePreviewModal()"></button>
      </div>
      <div class="modal-body" *ngIf="previewReport()">
        <ng-container *ngIf="previewLoading(); else previewContent">
          <div class="text-center py-5">
            <div class="spinner-border text-primary mb-3" role="status"></div>
            <p class="text-muted">Loading report preview...</p>
          </div>
        </ng-container>

        <ng-template #previewContent>
          <!-- Report Header -->
          <div class="report-preview-header mb-4">
            <div class="d-flex justify-content-between align-items-start mb-3">
              <div>
                <h4 class="mb-1">{{ previewReport()?.name || getReportTypeLabel(previewReport()?.reportType || '') }}</h4>
                <p class="text-muted mb-0">{{ formatDateRange(previewReport()?.startDate || '', previewReport()?.endDate || '') }}</p>
              </div>
              <div class="text-end">
                <span class="badge" [ngClass]="previewReport()?.status | statusBadge:'report'">
                  {{ getStatusText(previewReport()?.status || '') }}
                </span>
                <p class="small text-muted mt-1 mb-0">Generated: {{ previewReport()?.createdAt | adnDate:'datetime' }}</p>
              </div>
            </div>
          </div>

          <!-- Report Summary -->
          <div class="card mb-4">
            <div class="card-header">
              <h6 class="mb-0"><i class="bi bi-bar-chart me-2"></i>Report Summary</h6>
            </div>
            <div class="card-body">
              <div class="row g-3">
                <div class="col-md-3">
                  <div class="summary-stat">
                    <div class="summary-stat-value">{{ previewReport()?.recordCount || 0 }}</div>
                    <div class="summary-stat-label">Total Records</div>
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="summary-stat">
                    <div class="summary-stat-value">{{ previewReport()?.fileSize || 'N/A' }}</div>
                    <div class="summary-stat-label">File Size</div>
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="summary-stat">
                    <div class="summary-stat-value">PDF</div>
                    <div class="summary-stat-label">Format</div>
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="summary-stat">
                    <div class="summary-stat-value">{{ previewReport()?.status === 'submitted' ? 'Yes' : 'No' }}</div>
                    <div class="summary-stat-label">Submitted</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Report Options -->
          <div class="card mb-4">
            <div class="card-header">
              <h6 class="mb-0"><i class="bi bi-gear me-2"></i>Report Options</h6>
            </div>
            <div class="card-body">
              <div class="row g-2">
                <div class="col-12 col-sm-6 col-lg-3">
                  <div class="option-item" [class.active]="previewReport()?.options?.includeFlightLogs">
                    <i class="bi" [ngClass]="previewReport()?.options?.includeFlightLogs ? 'bi-check-circle-fill text-success' : 'bi-x-circle text-muted'"></i>
                    <span>Flight Logs</span>
                  </div>
                </div>
                <div class="col-12 col-sm-6 col-lg-3">
                  <div class="option-item" [class.active]="previewReport()?.options?.includeSprayRecords">
                    <i class="bi" [ngClass]="previewReport()?.options?.includeSprayRecords ? 'bi-check-circle-fill text-success' : 'bi-x-circle text-muted'"></i>
                    <span>Spray Records</span>
                  </div>
                </div>
                <div class="col-12 col-sm-6 col-lg-3">
                  <div class="option-item" [class.active]="previewReport()?.options?.includePilotInfo">
                    <i class="bi" [ngClass]="previewReport()?.options?.includePilotInfo ? 'bi-check-circle-fill text-success' : 'bi-x-circle text-muted'"></i>
                    <span>Pilot Info</span>
                  </div>
                </div>
                <div class="col-12 col-sm-6 col-lg-3">
                  <div class="option-item" [class.active]="previewReport()?.options?.includeAircraftInfo">
                    <i class="bi" [ngClass]="previewReport()?.options?.includeAircraftInfo ? 'bi-check-circle-fill text-success' : 'bi-x-circle text-muted'"></i>
                    <span>Aircraft Info</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </ng-template>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-outline-secondary" (click)="closePreviewModal()">Close</button>
        <button
          *ngIf="previewReport() && (previewReport()?.status === 'ready' || previewReport()?.status === 'submitted')"
          type="button"
          class="btn btn-primary"
          (click)="downloadReport(previewReport()!)"
        >
          <i class="bi bi-download me-2"></i>Download PDF
        </button>
        <button
          *ngIf="previewReport() && previewReport()?.status === 'draft'"
          type="button"
          class="btn btn-primary"
          (click)="generatePdf(previewReport()!); closePreviewModal()"
        >
          <i class="bi bi-file-earmark-pdf me-2"></i>Generate PDF
        </button>
      </div>
    </div>
  </div>
</div>

<!-- Email Modal (shared component) -->
<app-email-report-modal
  reportKind="faa"
  [isOpen]="showEmailModal()"
  [report]="emailReport()"
  [reportTitle]="emailReportTitle()"
  [reportSubtitle]="emailReportSubtitle()"
  (downloadRequested)="onEmailDownloadRequested()"
  (submitted)="onEmailSubmitted($event)"
  (closed)="closeEmailModal()"
></app-email-report-modal>

<!-- Delete Confirmation Modal -->
<div class="modal-backdrop fade show" *ngIf="showDeleteConfirm()" (click)="cancelDelete()"></div>
<div class="modal fade show d-block" tabindex="-1" *ngIf="showDeleteConfirm()">
  <div class="modal-dialog modal-dialog-centered" (click)="$event.stopPropagation()">
    <div class="modal-content">
      <div class="modal-header">
        <h5 class="modal-title">
          <i class="bi bi-exclamation-triangle text-danger me-2"></i>Delete Report
        </h5>
        <button type="button" class="btn-close" (click)="cancelDelete()"></button>
      </div>
      <div class="modal-body" *ngIf="reportToDelete()">
        <p>Are you sure you want to delete this report?</p>
        <div class="card bg-light">
          <div class="card-body py-2">
            <strong>{{ reportToDelete()?.name || getReportTypeLabel(reportToDelete()?.reportType || '') }}</strong>
            <br>
            <span class="text-muted small">{{ formatDateRange(reportToDelete()?.startDate || '', reportToDelete()?.endDate || '') }}</span>
          </div>
        </div>

        <!-- Delete error message -->
        <div *ngIf="deleteError()" class="alert alert-danger small mt-3 mb-0">
          <i class="bi bi-exclamation-circle me-1"></i>
          {{ deleteError() }}
        </div>

        <p *ngIf="!deleteError()" class="text-danger small mt-3 mb-0">
          <i class="bi bi-exclamation-circle me-1"></i>
          This action cannot be undone.
        </p>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-outline-secondary" (click)="cancelDelete()">Cancel</button>
        <button
          type="button"
          class="btn btn-danger"
          (click)="deleteReport()"
          [disabled]="deleteLoading()"
        >
          <ng-container *ngIf="!deleteLoading()">
            <i class="bi bi-trash me-2"></i>Delete
          </ng-container>
          <ng-container *ngIf="deleteLoading()">
            <span class="spinner-border spinner-border-sm me-2"></span>Deleting...
          </ng-container>
        </button>
      </div>
    </div>
  </div>
</div>
